import { Link, useLocation } from "react-router-dom";

import type { ProjectSummary } from "../api/projects";
import {
  costCenterHrefFromProjectKey,
  costCenterListHref,
  costCenterTypeFromProjectKey,
  costCenterTypeLabel,
  displayCostCenterKey,
} from "../lib/costCenterDisplay";
import { projectDisplay } from "../lib/projectDisplay";

export const COST_CENTER_MISSING_LABEL = "Nicht zugeordnet";

/** Cost center of one document position, shared by every table that lists positions. */
export default function CostCenterValue({
  projectKey,
  deliveryAddressDisplay,
  projectByKey,
  compact = false,
}: {
  projectKey: string | null | undefined;
  deliveryAddressDisplay?: string | null;
  projectByKey?: ReadonlyMap<string, ProjectSummary>;
  compact?: boolean;
}) {
  const location = useLocation();
  if (!projectKey) return <Missing />;

  const costCenterType = costCenterTypeFromProjectKey(projectKey);
  const display = projectDisplay(projectByKey?.get(projectKey), projectKey);
  const title =
    costCenterType === "delivery_address" && deliveryAddressDisplay
      ? deliveryAddressDisplay
      : costCenterType === "order_context"
        ? displayCostCenterKey(costCenterType, projectKey)
        : display?.title;
  if (!title) return <Missing />;
  const meta =
    costCenterType === "project" ? display?.meta : costCenterType === "order_context" ? null : costCenterTypeLabel(costCenterType);
  const href =
    costCenterHrefFromProjectKey(projectKey) ?? costCenterListHref(costCenterType);
  const tooltip = [
    title,
    meta,
    display?.tooltip && display.tooltip !== title ? display.tooltip : null,
  ]
    .filter(Boolean)
    .join("\n");

  return (
    <Link
      to={href}
      state={{ from: location.pathname }}
      className="block min-w-0 text-emerald-700 hover:underline"
      title={tooltip}
    >
      {compact ? (
        <span className="block truncate font-sans text-sm font-medium">
          {title}{meta ? <span className="font-normal text-emerald-800"> ({meta})</span> : null}
        </span>
      ) : (
        <>
          <span className="block truncate font-sans text-sm font-medium">{title}</span>
          {meta ? (
            <span className="block truncate font-mono text-xs text-emerald-800">{meta}</span>
          ) : null}
        </>
      )}
    </Link>
  );
}

function Missing() {
  return <span className="text-xs text-slate-500">{COST_CENTER_MISSING_LABEL}</span>;
}
